import { useEffect } from "react";

import { InnerPageChrome } from "@/components/layout/InnerPageChrome";
import { useDynamicArchive } from "@/data/dynamicPages";
import { useVorinAssets } from "@/pages/shared/useVorinAssets";
import { MINIMALISTIC_PORTFOLIO_STYLESHEETS } from "@/pages/shared/vorinDemoStyles";

import styles from "./DynamicPages.module.scss";

type DynamicArchivePageProps = {
  kind: "venue" | "menu";
  currentPath: string;
};

export function DynamicArchivePage({ kind, currentPath }: DynamicArchivePageProps) {
  useVorinAssets({ extraStylesheets: MINIMALISTIC_PORTFOLIO_STYLESHEETS });
  const { data, error } = useDynamicArchive(kind);

  useEffect(() => {
    if (!data) return;
    document.title = `${data.seoTitle || data.title} | VenueX`;
    document
      .querySelector('meta[name="description"]')
      ?.setAttribute("content", data.searchDescription || data.intro);
  }, [data]);

  if (error) {
    return (
      <InnerPageChrome currentPath={currentPath}>
        <main className={styles.statePage}>
          <span>VenueX</span>
          <h1>Archive unavailable.</h1>
          <p>{error}</p>
          <a href="/">Return home</a>
        </main>
      </InnerPageChrome>
    );
  }

  if (!data) {
    return (
      <InnerPageChrome currentPath={currentPath}>
        <main className={styles.loadingPage} aria-label="Loading archive">
          <span></span><span></span><span></span>
        </main>
      </InnerPageChrome>
    );
  }

  const content = data.presentation;

  return (
    <InnerPageChrome currentPath={currentPath}>
      <article className={styles.page}>
        <header className={styles.archiveHero}>
          <span className={styles.eyebrow}>{content.eyebrow}</span>
          <div className={styles.archiveHeroGrid}>
            <h1>{content.hero_title}</h1>
            <p>{content.intro || data.intro}</p>
          </div>
        </header>

        <section className={styles.archiveGridSection}>
          {data.items.length ? (
            <div className={styles.archiveGrid}>
              {data.items.map((item) => (
                <a className={styles.archiveCard} href={item.url} key={item.id}>
                  <div className={styles.archiveCardImage}>
                    {item.imageUrl && (
                      <img
                        src={item.imageUrl}
                        alt={item.imageAlt}
                        style={{ objectPosition: item.imagePosition }}
                        loading="lazy"
                      />
                    )}
                  </div>
                  <div className={styles.archiveCardBody}>
                    {item.meta.length > 0 && <span>{item.meta.join(" / ")}</span>}
                    <h2>{item.title}</h2>
                    <p>{item.summary}</p>
                  </div>
                </a>
              ))}
            </div>
          ) : (
            <p className={styles.emptyMessage}>{content.empty_message}</p>
          )}
        </section>

        <section className={styles.archiveCta}>
          <a className={styles.primaryLink} href={content.primary_link.href}>
            {content.primary_link.label} <span aria-hidden="true">&#8594;</span>
          </a>
          <a className={styles.secondaryLink} href={content.secondary_link.href}>
            {content.secondary_link.label}
          </a>
        </section>
      </article>
    </InnerPageChrome>
  );
}
